import makeStyles from '@mui/styles/makeStyles';
import type { Appearance } from '@stripe/stripe-js';

import { signupTokens, teal900 } from '@/theme/colors';

export const useStyles = makeStyles({
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 24px',
    borderTop: '1px solid var(--surfaceBorder)',
    backgroundColor: 'var(--surfaceSecondary)',
  },
  backButton: {
    color: 'var(--brandPrimary)',
    fontSize: 12,
    fontWeight: 500,
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  submitButton: {
    backgroundColor: teal900,
    color: 'white',
    fontSize: 14,
    fontWeight: 500,
    padding: '8px 24px !important',
    minHeight: '32px',
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: '#004544',
    },
    '&:disabled': {
      backgroundColor: 'var(--surfaceMuted)',
      color: 'var(--onSurfaceMuted)',
    },
  },
  // While a submit is in flight the button keeps its fill (plus a spinner) instead of the
  // muted disabled look, which read as "unavailable" rather than "working" (ENG-5549). The
  // spinner sets its color through sx because the theme pins MuiCircularProgress to dark green.
  submitButtonProcessing: {
    '&:disabled': {
      backgroundColor: teal900,
      color: 'white',
      // The teal Button variant fades every disabled state to 0.6.
      opacity: 1,
    },
  },
  // The form owns its own loading chrome so callers never have to gate their
  // layout on a callback that only fires on success. The height approximates the
  // card-details block it stands in for (label + PaymentElement + margin) so the
  // modal doesn't jump when Stripe resolves.
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    minHeight: 306,
  },
  loadingText: {
    fontSize: 14,
    color: 'var(--onSurfaceMuted)',
    margin: 0,
  },
  formContent: {
    padding: '16px 24px',
    backgroundColor: 'var(--surfaceCard)',
  },
  paymentElementWrapper: {
    marginBottom: 16,
  },
  paymentElementLabel: {
    display: 'block',
    fontSize: 14,
    color: 'var(--onSurfaceMuted)',
    marginBottom: 8,
  },
  paymentElementContainer: {
    border: '1px solid var(--surfaceBorder)',
    borderRadius: 4,
    padding: 8,
    backgroundColor: 'var(--surfaceCard)',
    minHeight: 242, // Reserve space for Stripe PaymentElement to prevent layout shift
    '@media (max-width: 600px)': {
      minHeight: 250, // Taller on mobile where fields stack vertically
    },
  },
  paymentError: {
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  paymentErrorHeading: {
    fontSize: 14,
    fontWeight: 600,
    color: signupTokens.errorText,
    margin: '0 0 16px 0',
  },
  paymentErrorText: {
    fontSize: 14,
    color: signupTokens.errorText,
    margin: '0 0 16px 0',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  // Without this the global `a` rule in theme/index.css applies teal
  // (--primaryLight) with no underline, which lands at 2.1:1 on this panel and
  // signals nothing non-visually — on the only route to support in a terminal
  // failure state.
  paymentErrorLink: {
    color: signupTokens.errorLink,
    textDecoration: 'underline',
    '&:hover': {
      color: signupTokens.errorLink,
    },
  },
});

// Stripe Elements render in an iframe and can't inherit CSS variables, so we
// resolve the surface tokens at appearance-build time and pass concrete values.
function readCssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export function buildStripeAppearance(resolvedTheme: 'light' | 'dark'): Appearance {
  return {
    theme: resolvedTheme === 'dark' ? 'night' : 'stripe',
    variables: {
      colorPrimary: teal900,
      colorBackground: readCssVar('--surfaceCard'),
      colorText: readCssVar('--onSurface'),
      colorTextPlaceholder: readCssVar('--onSurfaceMuted'),
      colorIcon: readCssVar('--onSurfaceMuted'),
      borderRadius: '0.4rem',
      fontSizeBase: '13px',
      spacingUnit: '3px',
    },
    rules: {
      '.Input': {
        padding: '8px 10px',
        fontSize: '13px',
      },
      '.Label': {
        fontSize: '13px',
        marginBottom: '4px',
      },
      '.Tab': {
        padding: '8px 12px',
        fontSize: '13px',
      },
      '.TabLabel': {
        fontSize: '13px',
      },
    },
  };
}
